import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Users,
  Calendar,
  CalendarClock,
  DollarSign,
  Clock,
  MessageSquare,
  FileText,
  Receipt,
} from "lucide-react";

interface ProviderSidebarProps {
  activeTab: string;
  onNavigateToTab: (tab: string) => void;
}

interface BadgeCounts {
  pendingPatients: number;
  unreadMessages: number;
}

const navGroups = [
  {
    label: "Clinical",
    items: [
      { id: "schedule", label: "Schedule", icon: CalendarClock },
      { id: "patients", label: "Patients", icon: Users, countKey: "pendingPatients" as const },
      { id: "appointments", label: "Appointments", icon: Calendar },
    ],
  },
  {
    label: "Operations",
    items: [
      { id: "availability", label: "Availability", icon: Clock },
      { id: "messages", label: "Messages", icon: MessageSquare, countKey: "unreadMessages" as const },
    ],
  },
  {
    label: "Finance",
    items: [
      { id: "payouts", label: "Payouts", icon: DollarSign },
      { id: "tax", label: "Tax Info", icon: Receipt },
    ],
  },
  {
    label: "Reference",
    items: [
      { id: "reference", label: "Peptide Reference", icon: FileText },
    ],
  },
];

const ProviderSidebar = ({ activeTab, onNavigateToTab }: ProviderSidebarProps) => {
  const { state, setOpenMobile, isMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const { user } = useAuth();
  const [counts, setCounts] = useState<BadgeCounts>({ pendingPatients: 0, unreadMessages: 0 });

  useEffect(() => {
    if (!user) return;
    const fetchCounts = async () => {
      const [patients, messages] = await Promise.all([
        supabase.from("health_assessments").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("messages").select("id", { count: "exact", head: true }).eq("recipient_id", user.id).eq("is_read", false),
      ]);
      setCounts({
        pendingPatients: patients.count || 0,
        unreadMessages: messages.count || 0,
      });
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="p-3">
        <div className="px-3 py-1.5">
          {!collapsed && (
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Provider</span>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">
              {group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const count = item.countKey ? counts[item.countKey] : 0;
                  const isActive = activeTab === item.id;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => {
                          onNavigateToTab(item.id);
                          if (isMobile) setOpenMobile(false);
                        }}
                        isActive={isActive}
                        tooltip={item.label}
                        className="cursor-pointer"
                      >
                        <item.icon size={16} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                      {count > 0 && !collapsed && (
                        <SidebarMenuBadge className="bg-destructive/10 text-destructive text-[10px] font-semibold">
                          {count}
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
};

export default ProviderSidebar;
